import { useTranslations } from 'next-intl';
import { ComparisonProps } from './types';
import { CustomImage as Image } from 'components/shared/custom-image';
import styles from './MobileTable.module.css';

type MobileProps = Array<ComparisonProps>;

export const MobileTable = ({
  className,
  data,
  selectedActivityTrackerId,
  uhRingPrice,
}: {
  className?: string;
  data: MobileProps;
  selectedActivityTrackerId: number;
  uhRingPrice: string;
}) => {
  const t = useTranslations('ring');
  return (
    <table className={[styles.root, className].filter(Boolean).join(' ')}>
        <colgroup>
          <col style={{ width: '50%' }} />
          <col style={{ width: '50%' }} />
        </colgroup>
        <tbody>
          {/* Form Section Header */}
          <tr className="top-border">
            <td className="property property-head" role="columnheader">
              <span className=" ">
                {t('ring.comparison.feature.form.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>

          {/* Weight Row */}
          <tr className="">
            <td className="property " role="rowheader">
              <span className=" ">
                {t('ring.comparison.feature.form.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/uh_weight.svg"
                    width={42}
                    height={42}
                    alt="weight-icon"
                    className="weight-icon"
                  />
                </div>
                <span className="bold-text">
                  {' '}
                  {/* 2.4 - 3.6 grams */}
                  {t('ring.comparison.uhRing.form.weight.bold')}
                </span>
                <span className="sub-bold-text">
                  {/* (varies by size) */}
                  {t('ring.comparison.uhRing.form.weight.sub')}
                </span>
                <span className="thin-text">
                  {/* Feels as heavy as a US penny */}
                  {t('ring.comparison.uhRing.form.weight.thin')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <Image
                  src={
                    data[selectedActivityTrackerId]?.weightIcon
                      ? (data[selectedActivityTrackerId].weightIcon as string)
                      : ''
                  }
                  width={selectedActivityTrackerId == 0 ? 36 : 42}
                  height={selectedActivityTrackerId == 0 ? 36 : 42}
                  alt="weight-icon"
                  className="weight-icon"
                />
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.weight
                    ? data[selectedActivityTrackerId].weight
                    : ''}
                </span>
                <span className="sub-bold-text">
                  {data[selectedActivityTrackerId]?.weightSub
                    ? data[selectedActivityTrackerId].weightSub
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.weightThin
                    ? data[selectedActivityTrackerId].weightThin
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr className="">
            <td className="property ">
              <span className=" ">
                {/* Form Factor */}
                {t('ring.comparison.feature.form.categoryB')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/formFator_ring.svg"
                  width={42}
                  height={42}
                  alt="ring-icon"
                  className="ring-icon"
                />

                <span className="bold-text">
                  {/* Ring */}
                  {t('ring.comparison.uhRing.form.formFactor.a')}
                  <br />
                  {/* no display */}
                  {t('ring.comparison.uhRing.form.formFactor.b')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <Image
                  src={
                    data[selectedActivityTrackerId]?.formFactorIcon
                      ? (data[selectedActivityTrackerId]
                          .formFactorIcon as string)
                      : ''
                  }
                  width={42}
                  height={42}
                  alt="form-factor-icon"
                  className="form-factor-icon"
                />
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.formFactor
                    ? data[selectedActivityTrackerId].formFactor
                    : ''}
                  <br />
                  {data[selectedActivityTrackerId]?.formFactorDisplay
                    ? data[selectedActivityTrackerId].formFactorDisplay
                    : ''}
                </span>
              </div>
            </td>
          </tr>

          {/* Real-Time Metrics Section Header */}
          <tr className="top-border">
            <td className="property property-head" role="columnheader">
              <span className="">
                {/* Real-Time Metrics */}
                {t('ring.comparison.feature.realtime.head.a')}
                <br />
                {t('ring.comparison.feature.realtime.head.b')}
              </span>
            </td>
            <td className=""></td>
          </tr>

          <tr className="">
            <td className="property" role="rowheader">
              <span className="">
                {/* Heart Rate <br /> Monitoring */}
                {t('ring.comparison.feature.realtime.categoryA.a')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* Periodically */}
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrMonitoring.bold',
                  )}
                </span>
                <span className="thin-text">
                  {' '}
                  {/* throughout the day */}
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrMonitoring.thin',
                  )}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.heartRateMonitoring
                    ? data[selectedActivityTrackerId].heartRateMonitoring
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.heartRateMonitoringSub
                    ? data[selectedActivityTrackerId].heartRateMonitoringSub
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property" role="rowheader">
              <span className="">
                {/* HRV Monitoring */}
                {t('ring.comparison.feature.realtime.categoryB')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* 24/7 HRV monitoring */}
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrvMonitoring.bold',
                  )}
                </span>
                <span className="thin-text">
                  {/* Dynamic and actionable */}
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrvMonitoring.thin',
                  )}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.hrvMonitoringRtm
                    ? data[selectedActivityTrackerId]?.hrvMonitoringRtm
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.hrvMonitoringRtmSub
                    ? data[selectedActivityTrackerId]?.hrvMonitoringRtmSub
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property" role="rowheader">
              <span className="">
                {/* Skin Temperature <br /> Tracking */}
                {t('ring.comparison.feature.realtime.categoryC.a')}
                <br />
                {t('ring.comparison.feature.realtime.categoryC.b')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* 24/7 */}
                  {t('ring.comparison.uhRing.realTimeMetrics.skinTemp.bold')}
                </span>
                <span className="thin-text">
                  {/* temperature monitoring */}
                  {t('ring.comparison.uhRing.realTimeMetrics.skinTemp.thin')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.skinTempTracking
                    ? data[selectedActivityTrackerId]?.skinTempTracking
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.skinTempTrackingSub
                    ? data[selectedActivityTrackerId]?.skinTempTrackingSub
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          {/* Sleep Section Header */}
          <tr className="top-border">
            <td className="property property-head" role="columnheader">
              <span className="">
                {/* Sleep */}
                {t('ring.comparison.feature.sleep.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr className="">
            <td className="property" role="rowheader">
              <span className="">
                {/* Sleep Tracking */}
                {t('ring.comparison.feature.sleep.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {/* <span className="bold-text"> */}
                {data[selectedActivityTrackerId]?.sleepTracking ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={30}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
                {/* </span> */}
              </div>
            </td>
          </tr>
          <tr className="">
            <td className="property" role="rowheader">
              <span className="">
                {/* Sleep Metrics */}
                {t('ring.comparison.feature.sleep.categoryB')}{' '}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* Comprehensive Sleep Stages Tracking with 10+ Contributing
                  Metrics */}
                  {t('ring.comparison.uhRing.sleep.sleepMetrics')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.sleepMetrics
                    ? data[selectedActivityTrackerId].sleepMetrics
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          {/* Movement & Workout Section Header */}
          <tr className="top-border">
            <td className="property property-head" role="columnheader">
              <span className="">
                {/* Movement & Workout */}
                {t('ring.comparison.feature.movementWorkout.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr>
            <td className="property" role="rowheader">
              <span className="">
                {/* Step Counting */}
                {t('ring.comparison.feature.movementWorkout.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {' '}
                {data[selectedActivityTrackerId]?.stepCounting ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Distance and Calories */}
                {t('ring.comparison.feature.movementWorkout.categoryB')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {' '}
                {data[selectedActivityTrackerId]?.distance ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
              </div>
            </td>
          </tr>
          {/* <tr>
            <td className="property">
              <span className="">Calories</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {' '}
                {data[selectedActivityTrackerId]?.calories ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
              </div>
            </td>
          </tr> */}

          <tr>
            <td className="property">
              <span className="">
                {/* VO2 Max */}
                {t('ring.comparison.feature.movementWorkout.categoryC')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {/* <span className="bold-text"> */}
                {data[selectedActivityTrackerId]?.vo2Max ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
                {/* </span> */}
              </div>
            </td>
          </tr>
          <tr className="top-border">
            <td className="property property-head">
              <span className="">
                {/* Recovery */}
                {t('ring.comparison.feature.recovery.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr className="">
            <td className="property">
              <span className="">
                {t('ring.comparison.feature.recovery.categoryA')}
                {/* HRV Metrics */}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* Dynamic Recovery tracking via SDNN, RMSSD metrics */}
                  {t('ring.comparison.uhRing.recovery.hrvMetrics')}
                </span>
                {/* <span className="thin-text">throughout the day</span> */}
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.hrvMetrics
                    ? data[selectedActivityTrackerId].hrvMetrics
                    : ''}
                </span>
                {/* <span className="thin-text">
                  {data[selectedActivityTrackerId]?.heartRateMonitoringSub
                    ? data[selectedActivityTrackerId].heartRateMonitoringSub
                    : ''}
                </span> */}
              </div>
            </td>
          </tr>
          {/* <tr>
            <td className="property">
              <span className="">HRV Monitoring</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">Dynamic and actionable</span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.hrvMonitoring
                    ? data[selectedActivityTrackerId]?.hrvMonitoring
                    : ''}
                </span>
              </div>
            </td>
          </tr> */}
          {/* <tr>
            <td className="property">
              <span className="">Temperature Tracking</span>
            </td>
          </tr> */}
          {/* <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
                <span className="thin-text">
                  Instaneous and accurate with no-contact temperature sensors.
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                {data[selectedActivityTrackerId]?.temperatureTracking ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark.svg"
                    width={28}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                )}
              </div>
            </td>
          </tr> */}
          <tr>
            <td className="property">
              <span className="">
                {/* Stress Monitoring */}
                {t('ring.comparison.feature.recovery.categoryC')}
              </span>
            </td>
          </tr>
          <tr>
            {/* <td className="property">
              <span className="">Stress Monitoring</span>
            </td> */}
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.stressMonitoring ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          {/* <tr>
            <td className="property">
              <span className="">SpO2 Monitoring</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.spo2Monitoring ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr> */}
          {/* <tr>
            <td className="property">
              <span className="">Respiratory Rate</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.respiratoryRate ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr> */}
          <tr className="top-border">
            <td className="property property-head">
              <span className="">
                {/* Function */}
                {t('ring.comparison.feature.function.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Battery Life */}
                {t('ring.comparison.feature.function.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/uh_battery.svg"
                  width={52}
                  height={24.9}
                  alt="uh-battery-icon"
                  className="uh-battery-icon"
                />
                <span className="bold-text">
                  {/* Up to 6 days */}
                  {t('ring.comparison.uhRing.function.batteryLife.bold')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <Image
                  src={
                    data[selectedActivityTrackerId]?.batteryLifeIcon
                      ? (data[selectedActivityTrackerId]
                          .batteryLifeIcon as string)
                      : ''
                  }
                  width={52}
                  height={24.9}
                  alt="battery-icon"
                  className="battery-icon"
                />
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.batteryLife
                    ? data[selectedActivityTrackerId]?.batteryLife
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.batteryLifeSub
                    ? data[selectedActivityTrackerId]?.batteryLifeSub
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Full Charging <br /> Time */}
                {t('ring.comparison.feature.function.categoryB.a')}{' '}
                {t('ring.comparison.feature.function.categoryB.b')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* 90 minutes */}
                  {t('ring.comparison.uhRing.function.fullChargingTime.bold')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.fullChargingTime
                    ? data[selectedActivityTrackerId]?.fullChargingTime
                    : ''}
                </span>
                <span className="thin-text">
                  {data[selectedActivityTrackerId]?.fullChargingSub
                    ? data[selectedActivityTrackerId]?.fullChargingSub
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Water Proof */}
                {t('ring.comparison.feature.function.categoryC')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* Up to 100 meters (about 328 feet) for up to 12 hours */}
                  {t('ring.comparison.uhRing.function.waterProof.bold')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.waterproof
                    ? data[selectedActivityTrackerId]?.waterproof
                    : ''}
                </span>
                {/* <span className="thin-text">
                  {data[selectedActivityTrackerId]?.waterproof
                    ? data[selectedActivityTrackerId]?.waterproof
                    : ''}
                </span> */}
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* GPS */}
                {t('ring.comparison.feature.function.categoryD')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/crossmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.gps ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Airplane Mode */}
                {t('ring.comparison.feature.function.categoryE')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.airplaneMode ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Find My Device */}
                {t('ring.comparison.feature.function.categoryF')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {data[selectedActivityTrackerId]?.findMyDevice ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
              </div>
            </td>
          </tr>
          {/* <tr>
            <td className="property">
              <span className="">Goal Tracking</span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                {data[selectedActivityTrackerId]?.goalTracking ? (
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                ) : (
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                )}
              </div>
            </td>
          </tr> */}
          <tr>
            <td className="property">
              <span className="">
                {/* Real-time
                <br /> Actionable Nudges */}
                {t('ring.comparison.feature.function.categoryH.a')}{' '}
                {t('ring.comparison.feature.function.categoryH.b')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.realTimeNotifications ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr className="top-border">
            <td className="property property-head">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.head')}
                {/* Unique value proposition */}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Form Factor */}
                {t('ring.comparison.feature.uniqueValProp.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              {' '}
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/formFator_ring.svg"
                  width={42}
                  height={42}
                  alt="ring-icon"
                  className="ring-icon"
                />
                <span className="bold-text">
                  {t('ring.comparison.uhRing.form.formFactor.a')}
                  <br />
                  {t('ring.comparison.uhRing.form.formFactor.b')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <Image
                  src={
                    data[selectedActivityTrackerId]?.formFactorIcon
                      ? (data[selectedActivityTrackerId]
                          .formFactorIcon as string)
                      : ''
                  }
                  width={42}
                  height={42}
                  alt="form-factor-icon"
                  className="form-factor-icon"
                />
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.formFactor
                    ? data[selectedActivityTrackerId]?.formFactor
                    : ''}
                  <br />
                  {data[selectedActivityTrackerId]?.formFactorDisplay
                    ? data[selectedActivityTrackerId]?.formFactorDisplay
                    : ''}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Circadian Rhythm Tracking */}
                {t('ring.comparison.feature.uniqueValProp.categoryB')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.circadianRhythmTracking ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Temperature Based Ovulation */}
                {t('ring.comparison.feature.uniqueValProp.categoryG')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.tempBasedOvulation ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Caffeine Window Detection */}
                {t('ring.comparison.feature.uniqueValProp.categoryC')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.caffeineWindowDetection ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* AFib Detection */}
                {t('ring.comparison.feature.uniqueValProp.categoryD')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
                <span className="sub-bold-text">
                  {/* (limited regions) */}
                  {t('ring.comparison.uhRing.uniqueValProp.afibDetection')}
                </span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.afibDetection ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Mandatory Data Subscription */}
                {t('ring.comparison.feature.uniqueValProp.categoryE')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">
                  {/* Not required */}
                  {t('ring.comparison.feature.subscription.notReq')}{' '}
                </span>
              </div>
            </td>

            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.subscriptionRequired
                    ? data[selectedActivityTrackerId]?.subscriptionRequired
                    : null}
                </span>
              </div>
            </td>
          </tr>

          <tr>
            <td className="property">
              <span className="">
                {/* Mandatory Data Subscription */}
                {t('ring.comparison.feature.uniqueValProp.categoryF')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <Image
                  src="web_v2/ring-buy/comparison/checkmark_new.svg"
                  width={30}
                  height={31}
                  alt="checkmark-icon"
                  className="checkmark-icon"
                />
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.performanceCoach ? (
                    <Image
                      src="web_v2/ring-buy/comparison/checkmark_new.svg"
                      width={30}
                      height={31}
                      alt="checkmark-icon"
                      className="checkmark-icon"
                    />
                  ) : (
                    <Image
                      src="web_v2/ring-buy/comparison/crossmark_new.svg"
                      width={28}
                      height={31}
                      alt="cross-icon"
                      className="cross-icon"
                    />
                  )}
                </span>
              </div>
            </td>
          </tr>
          <tr className="top-border">
            <td className="property property-head">
              <span className="">
                {' '}
                {/* Pricing */}
                {t('ring.comparison.feature.pricing.head')}
              </span>
            </td>
            <td className=""></td>
          </tr>
          <tr>
            <td className="property">
              <span className="">
                {/* Price Range */}
                {t('ring.comparison.feature.pricing.categoryA')}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div className="content">
                <span className="bold-text">{uhRingPrice}</span>
              </div>
            </td>
            <td>
              <div className="content">
                <span className="bold-text">
                  {data[selectedActivityTrackerId]?.priceRange
                    ? data[selectedActivityTrackerId]?.priceRange
                    : t('mobileTable.text.no')}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );
};
